// ReviewRecords.js
import {
    Avatar,
    Box,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Typography
} from '@mui/material';
import { getLocaleDateWithTimeCustom, getUserFullName } from 'app/utils/appHelpers';
import config from 'config';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import CustomTooltipWithContent from '../Tooltip/CustomTooltipWithContent';


function PlainReviewList({ records }) {
    return (

        <Box sx={{ maxHeight: '310px', overflowY: 'auto' }}>
            <List>
                {records.map((record, index) => (
                    <ListItem key={index} alignItems="flex-start">
                        <ListItemAvatar>
                            <Avatar
                                sx={{ height: 35, width: 35 }}
                                alt={record.user ? getUserFullName(record.user) : ''}
                                src={
                                    record.user && record.user.profileImageUrl
                                        ? `${config.userAuthApiUrl}${record.user.profileImageUrl}`
                                        : undefined
                                }
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = config.userAuthApiUrl + config.defaultProfileImageUrl;
                                }}
                            />


                        </ListItemAvatar>
                        <ListItemText
                            primary={
                                <Typography variant="body2">
                                    {getUserFullName(record.user)}
                                </Typography>
                            }
                            secondary={
                                <>
                                    <Typography variant="body2" sx={{ fontSize: '12px' }} color="textPrimary">
                                        {record.info}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        {getLocaleDateWithTimeCustom('tr', record.pdate)}
                                    </Typography>
                                </>
                            }
                        />
                    </ListItem>
                ))}
            </List>
        </Box>
    );
}



PlainReviewList.propTypes = {
    records: PropTypes.array.isRequired,
};

function ReviewRecords({ n, viewType }) {

    const userList = useSelector(state => state.user.data || []);

    if (!n || !n.userStatusId) return null;

    let reviewIds = n.userStatusId ? n.userStatusId.split(',') : [];
    let reviewInfos = n.userStatusInfo ? n.userStatusInfo.split(',') : [];
    let reviewPdates = n.userStatusPdate ? n.userStatusPdate.split(',') : [];
    let reviewUserIds = n.statusReviewUserIds ? n.statusReviewUserIds.split(',') : [];

    const normalizeArray = (arr) => (arr || []).map((x) => (x !== undefined && x !== null ? String(x).trim() : ""));
    reviewIds = normalizeArray(reviewIds);
    reviewInfos = normalizeArray(reviewInfos);
    reviewPdates = normalizeArray(reviewPdates);
    reviewUserIds = normalizeArray(reviewUserIds);

    if (reviewUserIds.length === 1 && reviewIds.length > 1) {
        reviewUserIds = Array(reviewIds.length).fill(reviewUserIds[0]);
    }
    if (reviewUserIds.length > 0 && reviewUserIds.length < reviewIds.length) {
        const lastUserId = reviewUserIds[reviewUserIds.length - 1];
        while (reviewUserIds.length < reviewIds.length) {
            reviewUserIds.push(lastUserId);
        }
    }



    const reviewRecords = reviewIds
        .map((id, i) => ({
            id: id ? id.trim() : "",
            info: typeof reviewInfos[i] !== 'undefined' ? reviewInfos[i].trim() : "",
            pdate: typeof reviewPdates[i] !== 'undefined' ? reviewPdates[i].trim() : "",
            userId: typeof reviewUserIds[i] !== 'undefined' ? reviewUserIds[i].trim() : ""
        }))
        .filter((record) => record && record.id !== "");

    if (viewType === 'card') {

        const reviewRecordsWithUser = reviewRecords.map((record) => {

            const user = userList.find((item) => item.id === Number(record.userId));
            return { ...record, user };
        });

        return <PlainReviewList records={reviewRecordsWithUser} />;

    } else if (viewType === 'datatable') {


        const validReviewRecords = reviewRecords.filter(
            (record) => record.userId && record.userId.trim() !== ''
        );

        const groups = validReviewRecords.reduce((acc, record) => {
            const uid = record.userId;
            if (!acc[uid]) acc[uid] = [];
            acc[uid].push(record);
            return acc;
        }, {});

        const uidKeys = Object.keys(groups).filter((uid) => uid && uid.trim() !== '');

        if (uidKeys.length === 0) return null;

        const renderItem = (uid) => {
            const groupRecords = groups[uid];

            const sortedGroupRecords = [...groupRecords].sort(
                (a, b) => new Date(b.pdate) - new Date(a.pdate)
            );
            const groupInfo = sortedGroupRecords.map((rec) => rec.info);
            const groupPdate = sortedGroupRecords.map((rec) => rec.pdate);
            const user = userList.find((u) => Number(u.id) === Number(uid));

            return (
                <CustomTooltipWithContent
                    key={uid}
                    reviewUserInfo={groupInfo}
                    reviewUserPdate={groupPdate}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '0' }}>
                        <Avatar
                            sx={{ height: 30, width: 30, mb: 1 }}
                            alt={user ? getUserFullName(user) : ''}
                            src={user && user.profileImageUrl ? `${config.userAuthApiUrl}${user.profileImageUrl}` : undefined}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = config.userAuthApiUrl + config.defaultProfileImageUrl;
                            }}
                        />
                        <Typography variant="caption">
                            {getUserFullName(user)}
                        </Typography>
                    </div>
                </CustomTooltipWithContent>
            );
        };

        // Tek kullanıcı varsa tek avatar göster
        if (uidKeys.length === 1) {
            return renderItem(uidKeys[0]);
        }

        // Farklı kullanıcılar varsa avatarları yan yana göster
        return (
            <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                {uidKeys.map((uid) => renderItem(uid))}
            </div>
        );

    }

    return null;
};

ReviewRecords.propTypes = {
    n: PropTypes.object.isRequired,
    viewType: PropTypes.oneOf(['card', 'datatable']).isRequired,
};

export default ReviewRecords;